import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpen, Loader2, Save, Sparkles, Trash2 } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/** GET /api/workspaces/:id/business-knowledge (server/worker-knowledge-routes.ts) */
interface BusinessKnowledge {
  workspaceId: number;
  content: string | null;
  source: "owner" | "auto" | null;
  updatedAt: string | null;
  updatedBy: string | null;
  /** the workspace falls back to the Insight playbook files when nothing is saved */
  insightDefault: boolean;
  effectiveSource: "owner" | "auto" | "insight-file" | "none";
  canEdit: boolean;
  maxChars: number;
  sections: string[];
}

/** apiRequest throws `Error("<status>: <body>")`; pull the server's `message` out of a JSON body when present. */
function serverMessage(err: unknown): string | undefined {
  const raw = err instanceof Error ? err.message : "";
  const body = raw.replace(/^\d+:\s*/, "");
  try {
    const parsed = JSON.parse(body) as { message?: unknown };
    if (typeof parsed.message === "string") return parsed.message;
  } catch { /* not JSON */ }
  return body || undefined;
}

const SOURCE_LABEL: Record<BusinessKnowledge["effectiveSource"], string> = {
  owner: "Saved by you",
  auto: "Drafted from your profile",
  "insight-file": "Insight playbook (maintained in the repo)",
  none: "Not drafted yet",
};

type Pending = null | "save" | "redraft" | "remove";

/**
 * Settings > Agents: the business knowledge document the Claude work team loads before every job
 * (GET /api/v1/agents/knowledge). Owners and admins can edit it, re-draft it from the workspace profile
 * or remove it; every overwrite is confirmed first.
 */
export function BusinessKnowledgeSetting({ workspaceId, canEdit }: { workspaceId: number; canEdit: boolean }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const queryKey = ["/api/workspaces", workspaceId, "business-knowledge"];
  const { data, isLoading, isError } = useQuery<BusinessKnowledge>({ queryKey, enabled: !!workspaceId });
  const [draft, setDraft] = useState("");
  const [confirm, setConfirm] = useState<Pending>(null);
  useEffect(() => { setDraft(data?.content ?? ""); }, [data?.content, workspaceId]);

  const editable = canEdit && data?.canEdit !== false;
  const refresh = (next?: Partial<BusinessKnowledge>) => {
    if (next) queryClient.setQueryData<BusinessKnowledge>(queryKey, (old) => (old ? { ...old, ...next } : old));
    queryClient.invalidateQueries({ queryKey });
  };

  const save = useMutation({
    mutationFn: async () => (await apiRequest("PUT", `/api/workspaces/${workspaceId}/business-knowledge`, { content: draft })).json(),
    onSuccess: (saved) => { refresh(saved); toast({ title: "Business knowledge saved", description: "The Claude team uses this version from its next job." }); },
    onError: (err) => toast({ title: "Could not save", description: serverMessage(err), variant: "destructive" }),
  });
  const redraft = useMutation({
    mutationFn: async () => (await apiRequest("POST", `/api/workspaces/${workspaceId}/business-knowledge/redraft`, { confirm: true })).json(),
    onSuccess: (saved) => { refresh(saved); setDraft(saved?.content ?? ""); toast({ title: "Re-drafted from your profile", description: "Read it through: anything marked \"unknown — ask the owner\" is not in your profile yet." }); },
    onError: (err) => toast({ title: "Could not re-draft", description: serverMessage(err), variant: "destructive" }),
  });
  const remove = useMutation({
    mutationFn: async () => (await apiRequest("DELETE", `/api/workspaces/${workspaceId}/business-knowledge`)).json(),
    onSuccess: () => { refresh({ content: null, source: null }); setDraft(""); toast({ title: "Saved knowledge removed" }); },
    onError: (err) => toast({ title: "Could not remove", description: serverMessage(err), variant: "destructive" }),
  });

  const busy = save.isPending || redraft.isPending || remove.isPending;
  const hasSaved = !!data?.content;
  const dirty = draft !== (data?.content ?? "");
  // An auto-draft the owner has read and found right can be saved unchanged: it becomes theirs ("Saved by you"), which also ticks
  // "Check your business knowledge page" off Draper home's Finish setup card.
  const confirmsAutoDraft = !dirty && data?.source === "auto" && !!data?.content;
  const tooLong = !!data && draft.length > data.maxChars;
  // A re-draft that overwrites a saved doc, and anything that replaces the Insight playbook files, asks first.
  const replacesInsightFiles = !!data?.insightDefault && !hasSaved;

  const run = (what: Exclude<Pending, null>) => {
    if (what === "save" && !replacesInsightFiles) return save.mutate();
    if (what === "redraft" && !hasSaved && !replacesInsightFiles) return redraft.mutate();
    setConfirm(what);
  };

  const confirmCopy: Record<Exclude<Pending, null>, { title: string; body: string; action: string }> = {
    save: {
      title: "Replace the Insight playbook?",
      body: "This workspace uses the Insight playbook kept in the repo. Saving here replaces it for every Claude job until you remove the saved doc.",
      action: "Save",
    },
    redraft: {
      title: "Re-draft from your profile?",
      body: replacesInsightFiles
        ? "A new draft built from the workspace profile would replace the Insight playbook for every Claude job. This makes one AI call."
        : "A new draft built from the workspace profile overwrites the saved document, including your edits. This makes one AI call.",
      action: "Re-draft",
    },
    remove: {
      title: "Remove the saved document?",
      body: data?.insightDefault
        ? "The Claude team goes back to the Insight playbook kept in the repo."
        : "The Claude team will draft a new one from your profile on its next job.",
      action: "Remove",
    },
  };

  return (
    <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-4" data-testid="section-business-knowledge">
      <div className="flex items-center gap-3 flex-wrap">
        <BookOpen size={20} className="text-amber-400" />
        <h2 className="font-display font-bold text-foreground">Business knowledge</h2>
        {data && (
          <span className="ml-auto text-[11px] px-2 py-0.5 rounded-full border border-border text-muted-foreground" data-testid="text-business-knowledge-source">
            {SOURCE_LABEL[data.effectiveSource]}
          </span>
        )}
      </div>
      <p className="text-xs text-muted-foreground/70">
        What the Claude work team knows about your business: services, audience, location, voice, the facts it may state, what it must never say or claim, your industry's rules and the sources to prefer. It reads this before every job and states nothing about your business that is not in it.
      </p>

      {isLoading && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
      {isError && <p className="text-xs text-red-400" data-testid="text-business-knowledge-error">Could not load the business knowledge.</p>}

      {data && (
        <>
          {data.insightDefault && !hasSaved && (
            <p className="text-[11px] text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2" data-testid="text-business-knowledge-insight">
              This workspace uses the Insight playbook kept in the repo (agents/insight/KNOWLEDGE.md and RULES.md). Leave this box empty to keep using it; saving a document here replaces it for the Claude team.
            </p>
          )}
          {data.effectiveSource === "none" && (
            <p className="text-[11px] text-muted-foreground/70" data-testid="text-business-knowledge-empty">
              No document yet. The Claude team drafts one from your profile on its first job, or you can re-draft it now and review it.
            </p>
          )}
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            readOnly={!editable}
            spellCheck={false}
            rows={18}
            placeholder={data.sections.map((s) => `## ${s}`).join("\n")}
            className="w-full font-mono text-xs bg-muted/20 border border-border rounded-xl p-3 text-foreground focus:outline-none focus:border-amber-500/40 read-only:opacity-80"
            data-testid="textarea-business-knowledge"
          />
          <div className="flex items-center gap-2 flex-wrap text-[11px] text-muted-foreground/60">
            <span data-testid="text-business-knowledge-chars">{draft.length.toLocaleString()} characters{tooLong ? ` (limit ${data.maxChars.toLocaleString()})` : ""}</span>
            {data.updatedAt && <span>· updated {new Date(data.updatedAt).toLocaleString()}</span>}
          </div>

          {editable ? (
            <div className="flex items-center gap-2 flex-wrap">
              <button
                data-testid="button-save-business-knowledge"
                onClick={() => run("save")}
                disabled={(!dirty && !confirmsAutoDraft) || !draft.trim() || tooLong || busy}
                className="flex items-center gap-2 px-4 py-2.5 bg-amber-600/20 text-amber-300 border border-amber-500/30 rounded-xl text-sm font-medium hover:bg-amber-600/30 transition-colors disabled:opacity-50 disabled:pointer-events-none"
              >
                {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {confirmsAutoDraft ? "It's right: save" : "Save"}
              </button>
              <button
                data-testid="button-redraft-business-knowledge"
                onClick={() => run("redraft")}
                disabled={busy}
                className="flex items-center gap-2 px-4 py-2.5 bg-muted/30 text-foreground border border-border rounded-xl text-sm font-medium hover:bg-muted/50 transition-colors disabled:opacity-50 disabled:pointer-events-none"
              >
                {redraft.isPending ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                Re-draft from profile
              </button>
              {hasSaved && (
                <button
                  data-testid="button-remove-business-knowledge"
                  onClick={() => run("remove")}
                  disabled={busy}
                  className="flex items-center gap-2 px-3 py-2.5 text-red-300/80 border border-red-500/20 rounded-xl text-sm hover:bg-red-500/10 transition-colors disabled:opacity-50 disabled:pointer-events-none ml-auto"
                >
                  {remove.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  Remove saved doc
                </button>
              )}
            </div>
          ) : (
            <p className="text-[11px] text-muted-foreground/60" data-testid="text-business-knowledge-readonly">Only a workspace owner or admin can edit the business knowledge.</p>
          )}
        </>
      )}

      <AlertDialog open={confirm !== null} onOpenChange={(open) => { if (!open) setConfirm(null); }}>
        {confirm && (
          <AlertDialogContent data-testid="dialog-business-knowledge-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>{confirmCopy[confirm].title}</AlertDialogTitle>
              <AlertDialogDescription>{confirmCopy[confirm].body}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="button-business-knowledge-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                data-testid="button-business-knowledge-confirm"
                onClick={() => {
                  const what = confirm;
                  setConfirm(null);
                  if (what === "save") save.mutate();
                  else if (what === "redraft") redraft.mutate();
                  else remove.mutate();
                }}
              >
                {confirmCopy[confirm].action}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        )}
      </AlertDialog>
    </div>
  );
}
